import styled from "@emotion/styled"
import { Container, Typography } from "@mui/material"
import {
  DisplayNameModel,
  PdfFileModel,
  ServicePlanModel,
  ServicePlansResponse,
} from "api"
import NextLink from "common/NextLink"
import {
  CommonForm,
  CommonSelectWithOnChange as CommonSelect,
  CommonSubmit,
} from "common/forms"

import Image from "next/image"
import { useState } from "react"
import Shared from "theme/shared"
import servicePlansSchema, { initialFormState } from "./ServicePlansSchema"

const Styled = {
  Wrapper: styled.div`
    text-align: left; /* flipped by mui */
  `,
  Title: styled(Typography)`
    margin-top: 40px;
  `,
  Subtitle: styled(Typography)`
    font-size: 16px;
    margin-top: 10px;
    color: ${(p) => p.theme.colors.marine};
    ${(p) => p.theme.breakpoints.up("md")} {
      font-size: 18px;
    }
  `,
  Description: styled(Typography)`
    font-size: 14px;
    margin-top: 10px;
    margin-bottom: 25px;
    color: ${(p) => p.theme.colors.bluishGrey};
    ${(p) => p.theme.breakpoints.up("md")} {
      margin-top: 5px;
    }
  `,
  InputWrapper: styled.div`
    max-width: 700px;
    display: flex;
    gap: 40px;
    justify-content: center;
    /* margin: 0 auto; */
    margin-top: 40px;
    margin-bottom: 20px;
    flex-direction: column;
    ${(p) => p.theme.breakpoints.up("md")} {
      flex-direction: row;
    }
  `,
  ResultsTitle: styled(Typography)`
    font-weight: bold;
    color: ${(p) => p.theme.colors.oceanBlueTwo};
  `,
}

const StyledSimpleDivider = styled(Shared.SimpleDivider)`
  margin-top: 20px;
  margin-bottom: 20px;
`

const StyledCommonSubmit = styled(CommonSubmit)`
  margin-right: 0;
  margin-left: 0;

  ${(p) => p.theme.breakpoints.up("md")} {
    width: 350px;
  }
`

const StyledResultsWrapper = styled.div`
  width: 680px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 40px 0;
`

const StyledNextLink = styled(NextLink)`
  display: flex;
  gap: 8px;
  align-items: center;
`
const StyledFileName = styled(Typography)`
  text-decoration: underline;
  word-break: break-all;
`

const StyledTelLink = styled.a`
  display: block;
  font-size: 16px;
  color: ${(p) => p.theme.colors.oceanBlueTwo};
  text-decoration: underline;
  unicode-bidi: plaintext;
  margin-bottom: 20px;
`

type ServicePlansPageProps = {
  data: ServicePlansResponse
}

const ServicePlansPage = ({ data }: ServicePlansPageProps) => {
  const [selectedModel, setSelectedModel] = useState<ServicePlanModel>(null)
  const [selectedDisplayName, setSelectedDisplayName] =
    useState<DisplayNameModel>(null)
  const [pdf, setPdf] = useState<PdfFileModel>(null)

  const modelsList =
    data?.modelList.sort((a, b) => a.modelName.localeCompare(b.modelName)) || []

  // MOCK DATA
  // const modelsList = [
  //   {
  //     modelName: "model1",
  //     displayList: [
  //       {
  //         displayName: "display1",
  //         pdf: { url: "url1", fileName: "fileName1" },
  //       },
  //     ],
  //   },
  //   {
  //     modelName: "model2",
  //     displayList: [
  //       {
  //         displayName: "display2",
  //         pdf: { url: "url2", fileName: "fileName2" },
  //       },
  //     ],
  //   },
  // ]
  const updateDate = new Date(
    data.lastUpdateDate || undefined,
  ).toLocaleDateString("en-GB")

  return (
    <>
      <Styled.Wrapper>
        <Container>
          <Styled.Title variant="h1">תוכנית טיפול מותאמת לרכב שלך</Styled.Title>
          <Styled.Subtitle>
            תוכנית הטיפול מציגה את רשימת החלקים שיש להחליף במעמד טיפול לרכבך.
            <br />
            הטיפול נדרש לביצוע באחד המקרים הבאים, המוקדם מבניהם: כשמופיעה התראה
            בלוח המחוונים של רכבך / לאחר נסיעה של 15,000 ק״מ / במידה וחלפה שנה
            ממועד הטיפול הקודם.
          </Styled.Subtitle>
          <StyledSimpleDivider />
          <Styled.Subtitle>
            להצגת תוכנית הטיפול, יש לבחור את דגם הרכב ושנת העלייה לכביש (מידע זה
            נמצא ברישיון הרכב).
          </Styled.Subtitle>
        </Container>
        <CommonForm
          initialValues={initialFormState}
          validationSchema={servicePlansSchema}
          onSubmit={async () => {
            if (!selectedDisplayName) return
            setPdf(selectedDisplayName.pdf)
          }}
          render={({ setFieldValue }) => {
            return (
              <Container>
                <Styled.InputWrapper>
                  <CommonSelect
                    name="select"
                    label="דגם רכב"
                    values={modelsList?.map((item) => item.modelName) || []}
                    map={(item) => ({ id: item, name: item })}
                    onChange={(e) => {
                      setFieldValue("select", e?.target?.value)
                      setSelectedModel(
                        modelsList.find(
                          (m) => m.modelName === e?.target?.value,
                        ),
                      )
                    }}
                    required
                  />
                  <CommonSelect
                    name="displaySelect"
                    label="שנת עלייה לכביש"
                    values={
                      selectedModel?.displayList?.map(
                        (item) => item.displayName,
                      ) || []
                    }
                    map={(item) => ({
                      id: item,
                      name: item,
                    })}
                    required
                    disabled={!selectedModel}
                    onChange={(e) => {
                      setFieldValue("displaySelect", e?.target?.value)
                      setSelectedDisplayName(
                        selectedModel?.displayList.find(
                          (m) => m.displayName === e?.target?.value,
                        ),
                      )
                    }}
                  />
                </Styled.InputWrapper>
                <StyledCommonSubmit
                  label="להצגת תוכנית הטיפול שלי"
                  align="center"
                />
              </Container>
            )
          }}
        />
        <Container>
          {pdf && (
            <StyledResultsWrapper>
              <Styled.ResultsTitle variant="h2">
                תוצאות חיפוש:
              </Styled.ResultsTitle>
              <StyledNextLink openInNewTab notEncoding href={pdf.url}>
                <Image
                  src="/images/filePdf.svg"
                  width={32}
                  height={32}
                  alt=""
                />
                <StyledFileName>{pdf.fileName}.pdf</StyledFileName>
              </StyledNextLink>
            </StyledResultsWrapper>
          )}
        </Container>
        <Container sx={{ marginBottom: "40px", marginTop: "60px" }}>
          <Styled.Title variant="h2" marginBottom={2}>
            שירות הלקוחות שלנו זמין בשבילך לכל עניין:
          </Styled.Title>
          <StyledTelLink href="tel:*2880">*2880</StyledTelLink>
          <Styled.Subtitle>
            המידע המוצג נכון לתאריך {updateDate} וייתכנו בו שינויים מעת לעת
            בהתאם להנחיית היצרן, תכנית הטיפולים עשויה להיות מושפעת ממאפייני
            הנהיגה, תנאי הדרך, האקלים, תחזוקת הרכב, קריאת מפתח הרכב ועוד. יתכן
            ויידרשו פעולות נוספות בהתאם לתוצאות הבדיקה ומצב הרכב
          </Styled.Subtitle>
        </Container>
      </Styled.Wrapper>
    </>
  )
}

export default ServicePlansPage
